import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { toast } from "react-toastify";
import './SliderContact.scss';
import useSlider from "../../../shared/components/Slider/useSlider";
import { Button, Checkbox, Column, Row } from "../../../components";
import * as lables from "../../../shared/util/label";
import { FloatingLabel, Form, InputGroup } from 'react-bootstrap';
import { Icon } from '../../../components';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/store";
import { useConfirmationModalContext } from "../../../shared/components/ModalConfirmationContext";
import CustomerDetailService from "../../customer-details/CustomerDetailService";
import { updateCustomers } from "../../customer-slice/Customer-slice";
import * as constant from "../../../shared/util/constant";
import Logger from "../../../shared/services/Logger";
import FormattedInputService from "../../../shared/components/input/formatted-input/FormattedInputService";
import { cleareTabsInputs, SliderDataAction, updateSliderData } from "../CustomerSliderContext";
import CustomerSliderContainerService from "../CustomerSliderContainerService";
import { AbilityContext } from "../../../shared/abilities/AbilityContext";
import { CommonUtility } from "../../../Services/CommonUtility";

export interface ISliderContact {
    customerId?: string
}

// methods exposed to parent
export interface SliderContactHandles {
    save: () => Promise<boolean>;
}

const SliderContactInner: React.ForwardRefRenderFunction<SliderContactHandles, ISliderContact> = (props, ref) => {

    const customerDetailService = new CustomerDetailService();
    const formattedInputService = new FormattedInputService();
    const customerSliderContainerService = new CustomerSliderContainerService();
    const dispatch = useDispatch();
    const slider = useSlider();

    const customerInfo = useSelector((state: RootState) => state?.customerSlice);
    const customerSliderState = useSelector((state: RootState) => state?.CustomerSliderState);

    const customerObjInStore = customerInfo.customer;
    const modalContext = useConfirmationModalContext();

    const customerDetail = customerSliderState.contactPage.customer;
    const formValidation = customerSliderState.contactPage.formValidation;

    const ability = useContext(AbilityContext);
    const canUserEditCustomer = ability.can('update', "Customer", constant.ScreenName.CUSTOMER);

    const TAB_INDEX_BASE = 1001;

    useEffect(() => {
        initializeComponent();
    }, [customerObjInStore]);

    const initializeComponent = async () => {

        // set contact page data only if customer info in main store chagnes.
        if (!customerDetail || !customerDetail.id) {

            if (customerObjInStore?.id) {
                dispatch(updateSliderData({ type: SliderDataAction.ContactUpdate, data: { ...customerObjInStore } }));
            }
            else {
                let cst = await customerDetailService.fetchCustomerById(props.customerId);
                if (cst) {
                    dispatch(updateCustomers(cst));
                }
            }

            dispatch(updateSliderData({ type: SliderDataAction.CustomerFormValidationUpdate, data: {} }));
        }
    };

    const toggleEntityField = (propName: string) => {
        let newValue = !customerDetail[propName];
        let updates: any = {
            [propName]: newValue,
        };
        
        // If SMS Opt Out is being checked, uncheck all phone SMS opt-ins
        if (propName === 'smsOptOut' && newValue === true) {
            updates.workPhoneSmsOptIn = false;
            updates.mobileSmsOptIn = false;
            updates.otherPhone1SmsOptIn = false;
            updates.otherPhone2SmsOptIn = false;
            updates.otherPhone3SmsOptIn = false;
        }

        let updatedCustomerData = {
            ...customerDetail,
            ...updates,
        };
        dispatch(updateSliderData({ type: SliderDataAction.ContactUpdate, data: updatedCustomerData }));

    }

    const onEntityFieldChange = (propName: string, value: string | Date | boolean) => {
        let updatedCustomerData = {
            ...customerDetail,
            [propName]: value,
        };

        dispatch(updateSliderData({ type: SliderDataAction.ContactUpdate, data: updatedCustomerData }));
    }

    const handleZipCodeValidation = (propName: string, val: string) => {
        let validationErrors = customerDetailService.validationForZipCode(propName, val);
        if (Object.keys(validationErrors).length > 0) {
            dispatch(updateSliderData({ type: SliderDataAction.CustomerFormValidationUpdate, data: { ...formValidation, ...validationErrors } }));
        } else {
            const newValidationErrors = { ...formValidation };
            delete newValidationErrors[propName];
            dispatch(updateSliderData({ type: SliderDataAction.CustomerFormValidationUpdate, data: newValidationErrors }));
        }
    }

    const handleEmailClick = (emailAddress) => {
        var result = customerDetailService.validateEmail(emailAddress);
        if (result) {
            window.open(`mailto:${emailAddress}`, '_blank', 'noopener,noreferrer');
        }
    };

    const onCancelClick = async () => {
        // dispatch(cleareTabsInputs());
        dispatch(updateSliderData({ type: SliderDataAction.ContactUpdate, data: { ...customerObjInStore } }));

    }

    // Expose save() to parent via ref
    useImperativeHandle(ref, () => ({
        save: async () => {
            return await onSaveClick();
        }
    }), [customerDetail, customerInfo]);

    const onSaveClick = async () => {
        try {
            let isAnyChange = customerSliderContainerService.isAnyUnsavedChangeInContacts(customerSliderState, customerObjInStore);

            if (!isAnyChange) {
                toast.warn(constant.SliderContactConstants.NO_CHANGES_TO_SAVE, {
                    position: toast.POSITION.TOP_RIGHT
                });

                return true;
            }

            let validationError = customerDetailService.validateCustomer(customerDetail);
            dispatch(updateSliderData({ type: SliderDataAction.CustomerFormValidationUpdate, data: validationError }));
            if (Object.keys(validationError).length > 0) {
                toast.warn(constant.CUSTOMER_FORM_VALIDATION_ERROR_MESSAGE, {
                    position: toast.POSITION.TOP_RIGHT
                });
                return false;
            }

            let updatedCustomer = await customerDetailService.saveCustomer(customerDetail);
            if (updatedCustomer) {
                toast.success(constant.CUSTOMER_SAVED_SUCCESSFULLY, {
                    position: toast.POSITION.TOP_RIGHT
                });
                dispatch(updateCustomers(updatedCustomer));
                return true;
            }
            else {
                toast.error(constant.CUSTOMER_ERROR_WHILE_SAVING, {
                    position: toast.POSITION.TOP_RIGHT
                });
                return false;
            }

        }
        catch (e) {
            Logger.logError(constant.CUSTOMER_ERROR_WHILE_SAVING, e);
            toast.error(constant.CUSTOMER_ERROR_WHILE_SAVING, {
                position: toast.POSITION.TOP_RIGHT
            });
            return false;
        }
    }

    let checkColumnLg = 12;
    let checkColumnMd = 12;

    let phoneInputColumnLg = 12;
    let phoneInputColumnMd = 12;

    let emailInputColumnLg = 12;
    let emailInputColumnMd = 12;

    const mainContennt = <>
        <Row>
            <Column lg={12} md={12} className="sld-col-section-header">
                <span className="sld-act-section-header">{"Main Contact"}</span>
            </Column>
        </Row>
        <Row>
            <Column lg={checkColumnLg} md={checkColumnMd}>
                <Row className="mb-2">
                    <Column lg={6} md={6}>
                        <Checkbox
                            id='sldcnctchkmailOptOut'
                            label={lables.CustomerScreentLables.DO_NOT_MAIL}
                            checked={customerDetail.mailOptOut}
                            onChange={() => toggleEntityField('mailOptOut')}
                            checkboxStyle={{ position: "relative", marginRight: "5px" }}
                            tabIndex={TAB_INDEX_BASE}
                        />
                    </Column>
                    <Column lg={6} md={6}>
                        <Checkbox
                            id='sldcnctchkcallOptOut'
                            label={lables.CustomerScreentLables.DO_NOT_CALL}
                            checked={customerDetail.callOptOut}
                            onChange={() => toggleEntityField('callOptOut')}
                            checkboxStyle={{ position: "relative", marginRight: "5px" }}
                            tabIndex={TAB_INDEX_BASE + 1}
                        />
                    </Column>
                </Row>

                <Row className="mb-2">
                    <Column lg={6} md={6}>
                        <Checkbox
                            id='sldcnctchkemailOptOut'
                            label={lables.CustomerScreentLables.DO_NOT_EMAIL}
                            checked={customerDetail.emailOptOut}
                            onChange={() => toggleEntityField('emailOptOut')}
                            checkboxStyle={{ position: "relative", marginRight: "5px" }}
                            tabIndex={TAB_INDEX_BASE + 2}
                        />
                    </Column>
                    <Column lg={6} md={6}>
                        <Checkbox
                            id='sldcnctchkeddOptIn'
                            label={lables.CustomerScreentLables.EDD_OPT_IN}
                            checked={customerDetail.eddOptIn}
                            onChange={() => toggleEntityField('eddOptIn')}
                            checkboxStyle={{ position: "relative", marginRight: "5px" }}
                            tabIndex={TAB_INDEX_BASE + 3}
                        />
                    </Column>
                </Row>

                <Row className="mb-2">
                    <Column lg={6} md={6}>
                        <Checkbox
                            id='sldcnctchksmsOptOut'
                            label={lables.CustomerScreentLables.SMS_OPT_OUT}
                            checked={customerDetail.smsOptOut}
                            onChange={() => toggleEntityField('smsOptOut')}
                            checkboxStyle={{ position: "relative", marginRight: "5px" }}
                            tabIndex={TAB_INDEX_BASE + 4}
                        />
                    </Column>
                    <Column lg={6} md={6}></Column>
                </Row>
            </Column>
        </Row>
        <hr />
        <div className="sld-div">
            <Row>
                        <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                                <div className="input-group has-validation mb-3">
                                        <span className="input-group-text remove-right-radius">
                                                <Icon icon="home" className="text-muted fa-fw" />
                                        </span>
                                        <FloatingLabel
                                                controlId="homePhone"
                                                label={lables.CustomerScreentLables.HOME_PHONE}
                                        >
                                                <Form.Control
                                                        type="tel"
                                                        placeholder=" "
                                                        name="homePhone"
                                                        value={customerDetail.homePhone ? formattedInputService.formatPhoneNumber(customerDetail.homePhone) : ""}
                                                        onChange={(changedValue) => onEntityFieldChange('homePhone', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                                        tabIndex={TAB_INDEX_BASE + 5}
                                                        maxLength={15}
                                                        isInvalid={formValidation['homePhone']?.isValid == false}
                                                />
                                                {formValidation['homePhone']?.isValid == false && (
                                                        <Form.Control.Feedback type="invalid">
                                                                {formValidation['homePhone']?.warningText}
                                                        </Form.Control.Feedback>
                                                )}
                                        </FloatingLabel>
                                </div>
                        </Column>
                </Row>
                <Row className="">
                        <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                                <div className="input-group has-validation mb-3">
                                        <span className="input-group-text remove-right-radius">
                                                <Icon icon="building" className="text-muted fa-fw" />
                                        </span>
                                        <FloatingLabel
                                                controlId="workPhone"
                                                label={lables.CustomerScreentLables.WORK_PHONE}
                                        >
                                                <Form.Control
                                                        type="tel"
                                                        placeholder=" "
                                                        name="workPhone"
                                                        onChange={(changedValue) => onEntityFieldChange('workPhone', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                                        value={customerDetail.workPhone ? formattedInputService.formatPhoneNumber(customerDetail.workPhone) : ""}
                                                        tabIndex={TAB_INDEX_BASE + 6}
                                                        maxLength={15}
                                                        isInvalid={formValidation['workPhone']?.isValid == false}
                                                        className="phone-input-checkbox-left"
                                                />
                                                {formValidation['workPhone']?.isValid == false && (
                                                        <Form.Control.Feedback type="invalid">
                                                                {formValidation['workPhone']?.warningText}
                                                        </Form.Control.Feedback>
                                                )}
                                        </FloatingLabel>
                                        <div className="input-group-text" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                                            <Checkbox
                                                id='sldWorkPhoneSmsOptIn'
                                                label={""}
                                                checked={customerDetail.workPhoneSmsOptIn}
                                                onChange={() => toggleEntityField('workPhoneSmsOptIn')}
                                                disabled={customerDetail.smsOptOut}
                                                className="checkbox-sms-optin"
                                                tabIndex={TAB_INDEX_BASE + 7}
                                            />
                                        </div>
                                </div>
                        </Column>
                </Row>
                <Row className="">
                        <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                                <div className="input-group has-validation mb-3">
                                        <span className="input-group-text remove-right-radius">
                                                <Icon icon="mobile" className="text-muted fa-fw" />
                                        </span>
                                        <FloatingLabel
                                                controlId="mobile"
                                                label={lables.CustomerScreentLables.CELL_PHONE}
                                        >
                                                <Form.Control
                                                        type="tel"
                                                        placeholder=" "
                                                        name="mobile"
                                                        value={customerDetail.mobile ? formattedInputService.formatPhoneNumber(customerDetail.mobile) : ""}
                                                        onChange={(changedValue) => onEntityFieldChange('mobile', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                                        tabIndex={TAB_INDEX_BASE + 8}
                                                        maxLength={15}
                                                        isInvalid={formValidation['Phone']?.isValid == false}
                                                        className="phone-input-checkbox-left"
                                                />
                                                {formValidation['Phone']?.isValid == false && (
                                                        <Form.Control.Feedback type="invalid">
                                                                {formValidation['Phone']?.warningText}
                                                        </Form.Control.Feedback>
                                                )}
                                        </FloatingLabel>
                                        <div className="input-group-text" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                                            <Checkbox
                                                id='sldMobileSmsOptIn'
                                                label={""}
                                                checked={customerDetail.mobileSmsOptIn}
                                                onChange={() => toggleEntityField('mobileSmsOptIn')}
                                                disabled={customerDetail.smsOptOut}
                                                className="checkbox-sms-optin"
                                                tabIndex={TAB_INDEX_BASE + 9}
                                            />
                                        </div>
                                </div>
                        </Column>
                </Row>

            {/*Other Phone 1*/}
            <Row className="">
                <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className="input-group-text remove-right-radius">
                            <Icon icon="building" className="text-muted fa-fw" />
                        </span>
                        <FloatingLabel
                            controlId="otherPhone1"
                            label={lables.CustomerScreentLables.OTHER_PHONE1}
                        >
                            <Form.Control
                                type="tel"
                                placeholder=" "
                                name="otherPhone1"
                                onChange={(changedValue) => onEntityFieldChange('otherPhone1', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                value={customerDetail.otherPhone1 ? formattedInputService.formatPhoneNumber(customerDetail.otherPhone1) : ""}
                                tabIndex={TAB_INDEX_BASE + 10}
                                maxLength={15}
                                isInvalid={formValidation['otherPhone1']?.isValid == false}
                                className="phone-input-checkbox-left"
                            />
                            {formValidation['otherPhone1']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['otherPhone1']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                        <div className="input-group-text" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                            <Checkbox
                                id='sldOtherPhone1SmsOptIn'
                                label={""}
                                checked={customerDetail.otherPhone1SmsOptIn}
                                onChange={() => toggleEntityField('otherPhone1SmsOptIn')}
                                disabled={customerDetail.smsOptOut}
                                className="checkbox-sms-optin"
                                tabIndex={TAB_INDEX_BASE + 11}
                            />
                        </div>
                    </div>
                </Column>
            </Row>

            {/*Other Phone 2*/}
            <Row className="">
                <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className="input-group-text remove-right-radius">
                            <Icon icon="building" className="text-muted fa-fw" />
                        </span>
                        <FloatingLabel
                            controlId="otherPhone2"
                            label={lables.CustomerScreentLables.OTHER_PHONE2}
                        >
                            <Form.Control
                                type="tel"
                                placeholder=" "
                                name="otherPhone2"
                                onChange={(changedValue) => onEntityFieldChange('otherPhone2', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                value={customerDetail.otherPhone2 ? formattedInputService.formatPhoneNumber(customerDetail.otherPhone2) : ""}
                                tabIndex={TAB_INDEX_BASE + 12}
                                maxLength={15}
                                isInvalid={formValidation['otherPhone2']?.isValid == false}
                                className="phone-input-checkbox-left"
                            />
                            {formValidation['otherPhone2']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['otherPhone2']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                        <div className="input-group-text" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                            <Checkbox
                                id='sldOtherPhone2SmsOptIn'
                                label={""}
                                checked={customerDetail.otherPhone2SmsOptIn}
                                onChange={() => toggleEntityField('otherPhone2SmsOptIn')}
                                disabled={customerDetail.smsOptOut}
                                className="checkbox-sms-optin"
                                tabIndex={TAB_INDEX_BASE + 13}
                            />
                        </div>
                    </div>
                </Column>
            </Row>

            {/*Other Phone 3*/}
            <Row className="">
                <Column lg={phoneInputColumnLg} md={phoneInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className="input-group-text remove-right-radius">
                            <Icon icon="building" className="text-muted fa-fw" />
                        </span>
                        <FloatingLabel
                            controlId="otherPhone3"
                            label={lables.CustomerScreentLables.OTHER_PHONE3}
                        >
                            <Form.Control
                                type="tel"
                                placeholder=" "
                                name="otherPhone3"
                                onChange={(changedValue) => onEntityFieldChange('otherPhone3', formattedInputService.getRawPhoneNumber(changedValue.currentTarget.value.toString()))}
                                value={customerDetail.otherPhone3 ? formattedInputService.formatPhoneNumber(customerDetail.otherPhone3) : ""}
                                tabIndex={TAB_INDEX_BASE + 14}
                                maxLength={15}
                                isInvalid={formValidation['otherPhone3']?.isValid == false}
                                className="phone-input-checkbox-left"
                            />
                            {formValidation['otherPhone3']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['otherPhone3']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                        <div className="input-group-text" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                            <Checkbox
                                id='sldOtherPhone3SmsOptIn'
                                label={""}
                                checked={customerDetail.otherPhone3SmsOptIn}
                                onChange={() => toggleEntityField('otherPhone3SmsOptIn')}
                                disabled={customerDetail.smsOptOut}
                                className="checkbox-sms-optin"
                                tabIndex={TAB_INDEX_BASE + 15}
                            />
                        </div>
                    </div>
                </Column>
            </Row>

            <Row className="">
                <Column lg={emailInputColumnLg} md={emailInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className={`input-group-text remove-right-radius ${customerDetail.eMail ? 'click-wrapper ' : ''}`} onClick={() => handleEmailClick(customerDetail.eMail ?? "")}>
                            <Icon icon="email" className={`text-muted fa-fw ${customerDetail.eMail ? 'customer-detail-icon' : ''}`} />
                        </span>
                        <FloatingLabel
                            controlId="email1"
                            label={lables.CustomerScreentLables.EMAIL_1}
                        >
                            <Form.Control
                                type="email"
                                placeholder=" "
                                name="eMail"
                                value={customerDetail.eMail ?? ""}
                                onChange={(event) => onEntityFieldChange('eMail', event.currentTarget.value)}
                                tabIndex={TAB_INDEX_BASE + 16}
                                maxLength={150}
                                isInvalid={formValidation['eMail']?.isValid == false}
                            />
                            {formValidation['eMail']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['eMail']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                    </div>            </Column>
            </Row>
            <Row className="">
                <Column lg={emailInputColumnLg} md={emailInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className={`input-group-text remove-right-radius ${customerDetail.eMail2 ? 'click-wrapper ' : ''}`} onClick={() => handleEmailClick(customerDetail.eMail2 ?? "")}>
                            <Icon icon="email" className={`text-muted fa-fw ${customerDetail.eMail2 ? 'customer-detail-icon' : ''}`} />
                        </span>
                        <FloatingLabel
                            controlId="email2"
                            label={lables.CustomerScreentLables.EMAIL_2}
                        >
                            <Form.Control
                                type="email"
                                placeholder=" "
                                name="eMail2"
                                value={customerDetail.eMail2 ?? ""}
                                onChange={(event) => onEntityFieldChange('eMail2', event.currentTarget.value)}
                                tabIndex={TAB_INDEX_BASE + 17}
                                maxLength={150}
                                isInvalid={formValidation['eMail2']?.isValid == false}
                            />
                            {formValidation['eMail2']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['eMail2']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                    </div>
                </Column>
            </Row>

            {/*EMail 3*/}
            <Row className="">
                <Column lg={emailInputColumnLg} md={emailInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className={`input-group-text remove-right-radius ${customerDetail.email3 ? 'click-wrapper ' : ''}`} onClick={() => handleEmailClick(customerDetail.email3 ?? "")}>
                            <Icon icon="email" className={`text-muted fa-fw ${customerDetail.email3 ? 'customer-detail-icon' : ''}`} />
                        </span>
                        <FloatingLabel
                            controlId="email3"
                            label={lables.CustomerScreentLables.EMAIL_3}
                        >
                            <Form.Control
                                type="email"
                                placeholder=" "
                                name="email3"
                                value={customerDetail.email3 ?? ""}
                                onChange={(event) => onEntityFieldChange('email3', event.currentTarget.value)}
                                tabIndex={TAB_INDEX_BASE + 18}
                                maxLength={150}
                                isInvalid={formValidation['email3']?.isValid == false}
                            />
                            {formValidation['email3']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['email3']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                    </div>
                </Column>
            </Row>

            {/*EMail 4*/}
            <Row className="">
                <Column lg={emailInputColumnLg} md={emailInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className={`input-group-text remove-right-radius ${customerDetail.email4 ? 'click-wrapper ' : ''}`} onClick={() => handleEmailClick(customerDetail.email4 ?? "")}>
                            <Icon icon="email" className={`text-muted fa-fw ${customerDetail.email4 ? 'customer-detail-icon' : ''}`} />
                        </span>
                        <FloatingLabel
                            controlId="email4"
                            label={lables.CustomerScreentLables.EMAIL_4}
                        >
                            <Form.Control
                                type="email"
                                placeholder=" "
                                name="email4"
                                value={customerDetail.email4 ?? ""}
                                onChange={(event) => onEntityFieldChange('email4', event.currentTarget.value)}
                                tabIndex={TAB_INDEX_BASE + 19}
                                maxLength={150}
                                isInvalid={formValidation['email4']?.isValid == false}
                            />
                            {formValidation['email4']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['email4']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                    </div>
                </Column>
            </Row>

            {/*EMail 5*/}
            <Row className="">
                <Column lg={emailInputColumnLg} md={emailInputColumnMd}>
                    <div className="input-group has-validation mb-3">
                        <span className={`input-group-text remove-right-radius ${customerDetail.email5 ? 'click-wrapper ' : ''}`} onClick={() => handleEmailClick(customerDetail.email5 ?? "")}>
                            <Icon icon="email" className={`text-muted fa-fw ${customerDetail.email5 ? 'customer-detail-icon' : ''}`} />
                        </span>
                        <FloatingLabel
                            controlId="email5"
                            label={lables.CustomerScreentLables.EMAIL_5}
                        >
                            <Form.Control
                                type="email"
                                placeholder=" "
                                name="email5"
                                value={customerDetail.email5 ?? ""}
                                onChange={(event) => onEntityFieldChange('email5', event.currentTarget.value)}
                                tabIndex={TAB_INDEX_BASE + 20}
                                maxLength={150}
                                isInvalid={formValidation['email5']?.isValid == false}
                            />
                            {formValidation['email5']?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['email5']?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </FloatingLabel>
                    </div>
                </Column>
            </Row>
        </div>
        <hr />
        <Row>
            <Column lg={12} md={12} className="sld-col-section-header mb-3">
                <span className="sld-act-section-header">Mailing Address</span>
            </Column>
        </Row>
        <Row className="mb-3">
            <Column lg={12} md={12}
                className={formValidation['address1']?.isValid == false ? 'feedback-row' : ''}>
                <FloatingLabel
                    controlId="sliderAddress1"
                    label={<>
                        {lables.CustomerScreentLables.ADDRESS_1}
                        <span className="text-danger ms-1">*</span>
                    </>}
                >
                    <Form.Control
                        type="text"
                        placeholder=" "
                        name="address1"
                        value={customerDetail.address1 ?? ""}
                        onChange={(event) => onEntityFieldChange('address1', event.currentTarget.value)}
                        disabled={!canUserEditCustomer}
                        required
                        tabIndex={TAB_INDEX_BASE + 21}
                        maxLength={100}
                        isInvalid={formValidation['address1']?.isValid == false}
                    />
                    {formValidation['address1']?.isValid == false && (
                        <Form.Control.Feedback type="invalid">
                            {formValidation['address1']?.warningText}
                        </Form.Control.Feedback>
                    )}
                </FloatingLabel>
            </Column>
        </Row>
        <Row className="mb-3">
            <Column lg={12} md={12}>
                <FloatingLabel
                    controlId="sliderAddress2"
                    label={lables.CustomerScreentLables.ADDRESS_2}
                >
                    <Form.Control
                        type="text"
                        placeholder=" "
                        name="address2"
                        value={customerDetail.address2 ?? ""}
                        onChange={(event) => onEntityFieldChange('address2', event.currentTarget.value)}
                        disabled={!canUserEditCustomer}
                        tabIndex={TAB_INDEX_BASE + 22}
                        maxLength={100}
                    />
                </FloatingLabel>
            </Column>
        </Row>
        <Row className="mb-3">
            <Column lg={6} md={6}
                className={formValidation['city']?.isValid == false ? 'feedback-row' : ''}>
                <FloatingLabel
                    controlId="sliderCity"
                    label={<>
                        {lables.CustomerScreentLables.CITY}
                        <span className="text-danger ms-1">*</span>
                    </>}
                >
                    <Form.Control
                        type="text"
                        placeholder=" "
                        name="city"
                        value={customerDetail.city ?? ""}
                        onChange={(event) => onEntityFieldChange('city', event.currentTarget.value)}
                        disabled={!canUserEditCustomer}
                        required
                        tabIndex={TAB_INDEX_BASE + 23}
                        maxLength={50}
                        isInvalid={formValidation['city']?.isValid == false}
                    />
                    {formValidation['city']?.isValid == false && (
                        <Form.Control.Feedback type="invalid">
                            {formValidation['city']?.warningText}
                        </Form.Control.Feedback>
                    )}
                </FloatingLabel>
            </Column>
            <Column lg={3} md={3}
                className={formValidation['state']?.isValid == false ? 'feedback-row' : ''}>
                <FloatingLabel
                    controlId="sliderState"
                    label={<>
                        {lables.CustomerScreentLables.STATE}
                        <span className="text-danger ms-1">*</span>
                    </>}
                >
                    <Form.Control
                        type="text"
                        placeholder=" "
                        name="state"
                        value={customerDetail.state ?? ""}
                        onChange={(event) => onEntityFieldChange('state', event.currentTarget.value)}
                        disabled={!canUserEditCustomer}
                        required
                        tabIndex={TAB_INDEX_BASE + 24}
                        maxLength={2}
                        isInvalid={formValidation['state']?.isValid == false}
                    />
                    {formValidation['state']?.isValid == false && (
                        <Form.Control.Feedback type="invalid">
                            {formValidation['state']?.warningText}
                        </Form.Control.Feedback>
                    )}
                </FloatingLabel>
            </Column>
            <Column lg={3} md={3}
                className={formValidation['zipCode']?.isValid == false ? 'feedback-row' : ''}>
                <FloatingLabel
                    controlId="sliderZipCode"
                    label={<>
                        {lables.CustomerScreentLables.ZIP_CODE}
                        <span className="text-danger ms-1">*</span>
                    </>}
                >
                    <Form.Control
                        type="text"
                        placeholder=" "
                        name="zipCode"
                        value={customerDetail.zipCode ? CommonUtility.zipCodeFormat(customerDetail.zipCode) : ""}
                        onChange={(event) => onEntityFieldChange('zipCode', formattedInputService.getRawZipCode(event.currentTarget.value))}
                        onBlur={() => handleZipCodeValidation('zipCode', customerDetail.zipCode ?? '')}
                        disabled={!canUserEditCustomer}
                        required
                        tabIndex={TAB_INDEX_BASE + 25}
                        maxLength={10}
                        isInvalid={formValidation['zipCode']?.isValid == false}
                    />
                    {formValidation['zipCode']?.isValid == false && (
                        <Form.Control.Feedback type="invalid">
                            {formValidation['zipCode']?.warningText}
                        </Form.Control.Feedback>
                    )}
                </FloatingLabel>
            </Column>
        </Row>
        <Row className="mb-2 sld-ctc-action-row">
            <Column lg={6} md={6}>
                <Button
                    className='btn btn-outline-tertiary sld-action-btn-base'
                    key="btnActivityCancel"
                    onClick={() => onCancelClick()}
                    tabIndex={TAB_INDEX_BASE + 26}
                    id="btnActivityCancel"
                    color='primary'
                    outlined={true}
                >{lables.StandardSearchLable.RESET.toLocaleUpperCase()}
                </Button>

            </Column>
            <Column lg={6} md={6} className="text-right">
                <Button
                    className='sld-action-btn-base'
                    key="btnActivitySave"
                    onClick={() => onSaveClick()}
                    tabIndex={TAB_INDEX_BASE + 27}
                    id="btnActivitySave"
                    color='primary'
                    disabled={!canUserEditCustomer}
                >{lables.SAVE.toLocaleUpperCase()}
                </Button>
            </Column>
        </Row>
    </>;

    return <div className="slider-tab-content-main-div">
        {mainContennt}
    </div>;
}

const SliderContact = forwardRef(SliderContactInner);
export default SliderContact;
export { SliderContactInner };